import { ProcedimentoOdontoSelectModel } from 'components/form/field/select/ProcedimentoOdontoSelectField'
import { resolveName, UseFieldProps } from 'components/form/final-form/useField'
import { ParteBucalEnum, SituacaoCoroaEnum } from 'graphql/types.generated'
import React, { useState } from 'react'
import { Field, FieldRenderProps } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import { getDentePosicaoProtese, isCoroaDisabled, possuiLesaoDeFurca } from '../../../util'
import { CoroaFaceEnum } from '../../odontograma/Coroa'
import { Odontograma } from '../../odontograma/Odontograma'
import { CoroaFaceModel, FillColor } from '../../odontograma/types/Coroa'
import { CondicoesDenteFieldModel } from '../components/CondicoesDenteField'
import { convertDentesFieldModelToDentesModel } from '../converter'
import { condicoesDeLesaoDeFurca } from '../model'
import { OdontogramaFieldModel } from '../OdontogramaField'
import DenteFormPopper, { DenteFormModel } from './DenteFormPopper'
import { CoroaFieldModel } from './types'

export interface DenteFieldModel {
  condicoesDente: CondicoesDenteFieldModel
  procedimentos?: ProcedimentoOdontoSelectModel[]
  observacoes: string[]
  coroa?: CoroaFieldModel<SituacaoCoroaEnum>
}

export interface DentesFieldProps extends UseFieldProps {
  name: MetaPath<Record<ParteBucalEnum, DenteFieldModel>>
  fillColors: FillColor<SituacaoCoroaEnum>[]
  cidadaoDataNascimento: LocalDate
  showDeciduos: boolean
  proteseTotalSuperior: boolean
  proteseTotalInferior: boolean
  initialValue?: OdontogramaFieldModel['dentes']
}

interface DenteActive {
  parteBucalEnum: ParteBucalEnum
  ref: SVGSVGElement
}

export const getCoroasDisableds = (values: [ParteBucalEnum, DenteFieldModel][]) => {
  return values.map(([key, dente]) => isCoroaDisabled(dente) && key).filter((i) => !!i)
}

export default function DentesField(props: DentesFieldProps) {
  const {
    name,
    fillColors,
    proteseTotalSuperior,
    proteseTotalInferior,
    showDeciduos,
    initialValue,
    cidadaoDataNascimento,
  } = props

  const [popperOpen, setPopperOpen] = useState(false)
  const [activeDente, setActiveDente] = useState<DenteActive>()

  const onClickDente = (dente: ParteBucalEnum, ref: SVGSVGElement) => {
    setActiveDente({ parteBucalEnum: dente, ref })
    setPopperOpen(true)
  }

  const updateCoroaFace = (face: CoroaFaceModel) => {
    if (face?.activeColor) {
      const index =
        fillColors.indexOf(
          fillColors.find((color) => {
            return color.id === face.activeColor.id
          })
        ) + 1

      return { ...face, activeColor: fillColors[index] }
    } else {
      return { ...face, activeColor: fillColors[0] }
    }
  }

  const renderField = (fieldProps: FieldRenderProps<OdontogramaFieldModel['dentes']>) => {
    const {
      input: { value, onChange },
    } = fieldProps

    const fieldValues = Object.entries(value) as [ParteBucalEnum, DenteFieldModel][]
    const coroasDisabled = getCoroasDisableds(fieldValues)

    const handleChange = (newValues: [ParteBucalEnum, DenteFieldModel][]) => {
      const o = Object.fromEntries(new Map([...fieldValues, ...newValues] as [ParteBucalEnum, DenteFieldModel][]))
      onChange(o)
    }

    const getDenteValue = (dente: ParteBucalEnum) => {
      const o = Object.entries(value).find(([key]) => key === dente)
      return o && o[1]
    }

    const onClickCoroa = (dente: ParteBucalEnum, face: CoroaFaceModel, faceEnum: CoroaFaceEnum) => {
      const denteValue = getDenteValue(dente)
      const newFace = updateCoroaFace(face)

      handleChange([
        [
          dente,
          {
            ...denteValue,
            coroa: {
              faceTop: faceEnum === CoroaFaceEnum.FaceTop ? newFace : denteValue?.coroa?.faceTop,
              faceLeft: faceEnum === CoroaFaceEnum.FaceLeft ? newFace : denteValue?.coroa?.faceLeft,
              faceRight: faceEnum === CoroaFaceEnum.FaceRight ? newFace : denteValue?.coroa?.faceRight,
              faceBottom: faceEnum === CoroaFaceEnum.FaceBottom ? newFace : denteValue?.coroa?.faceBottom,
              faceCenter: faceEnum === CoroaFaceEnum.FaceCenter ? newFace : denteValue?.coroa?.faceCenter,
            } as CoroaFieldModel<SituacaoCoroaEnum>,
          } as DenteFieldModel,
        ],
      ])
    }

    const handleSubmit = (formValues: DenteFormModel) => {
      const { replicarItems, ...values } = formValues

      setPopperOpen(false)

      values &&
        handleChange([
          ...((replicarItems &&
            replicarItems.map(
              (replicarItem) =>
                [
                  replicarItem.value,
                  {
                    ...value[replicarItem.value],
                    condicoesDente: possuiLesaoDeFurca(replicarItem.value)
                      ? values.condicoesDente
                      : values.condicoesDente.filter((item) => !condicoesDeLesaoDeFurca.includes(item)),
                  },
                ] as [ParteBucalEnum, DenteFieldModel]
            )) ||
            []),
          [activeDente.parteBucalEnum, values],
        ])

      setActiveDente(undefined)
    }

    return (
      <>
        <Odontograma
          activeDente={activeDente?.parteBucalEnum}
          showDeciduos={showDeciduos}
          fillColors={fillColors}
          onClickDente={onClickDente}
          onClickCoroa={onClickCoroa}
          value={{
            proteseTotalSuperior: proteseTotalSuperior,
            proteseTotalInferior: proteseTotalInferior,
            dentes: convertDentesFieldModelToDentesModel(value),
          }}
          disabledCoroas={coroasDisabled}
        />

        {activeDente && (
          <DenteFormPopper
            open={popperOpen}
            placement='right'
            cidadaoDataNascimento={cidadaoDataNascimento}
            dentesValues={value}
            initialValues={value[activeDente.parteBucalEnum]}
            anchorRef={activeDente.ref}
            parteBucalEnum={activeDente.parteBucalEnum}
            protese={
              getDentePosicaoProtese(activeDente.parteBucalEnum) === 'superior'
                ? proteseTotalSuperior
                : proteseTotalInferior
            }
            proteseTotalSuperior={proteseTotalSuperior}
            proteseTotalInferior={proteseTotalInferior}
            onSubmit={handleSubmit}
          />
        )}
      </>
    )
  }

  return (
    <Field name={resolveName(name)} render={renderField} initialValue={initialValue} subscription={{ value: true }} />
  )
}
